import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, categoryIcons } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileCompareOpen, setMobileCompareOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const navbar = document.getElementById("navbar");
      if (!navbar) return;
      const currentScrollY = window.scrollY;
      if (currentScrollY > 80) {
        navbar.classList.add("bg-background-50/95", "backdrop-blur-md");
        navbar.classList.remove("bg-transparent");
      } else {
        navbar.classList.remove("bg-background-50/95", "backdrop-blur-md");
        navbar.classList.add("bg-transparent");
      }
      lastScrollY = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileCatOpen(false);
    setMobileCompareOpen(false);
  };

  return (
    <nav
      id="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent"
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
              Kakobuy<span className="text-accent-600">Finds</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/kakobuy-spreadsheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Spreadsheet Guide
            </Link>
            <Link
              to="/spreadsheet-comparison"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Compare Platforms
            </Link>
            <Link
              to="/kakobuy-spreadsheet-download"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Download Sheet
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/80 overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                        {cat.name}
                        <i className="ri-arrow-right-up-line text-xs text-foreground-400 ml-auto"></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 ml-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 text-sm font-medium border border-foreground-300 text-foreground-800 rounded-full hover:bg-foreground-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Kakobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden relative w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-all duration-200 ${mobileOpen ? "ri-close-line rotate-90" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      <div
        className={`md:hidden fixed inset-0 top-16 bg-background-50 z-40 transition-all duration-300 ${
          mobileOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full pointer-events-none"
        }`}
      >
        <div className="h-full overflow-y-auto px-4 py-4 pb-24 space-y-2">
          <Link
            to="/"
            onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
            Home
          </Link>

          <Link
            to="/kakobuy-spreadsheet-guide"
            onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-guide-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
            Spreadsheet Guide
          </Link>

          <Link
            to="/kakobuy-spreadsheet-finds"
            onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-star-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
            Best Finds 2026
          </Link>

          <Link
            to="/kakobuy-spreadsheet-beginners"
            onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-book-open-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
            Beginners Guide
          </Link>

          <Link
            to="/kakobuy-spreadsheet-download"
            onClick={closeMobile}
            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
          >
            <i className="ri-download-2-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
            Download Sheet
          </Link>

          <div className="pt-3">
            <button
              onClick={() => setMobileCompareOpen(!mobileCompareOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-scales-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
                Compare Platforms
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCompareOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCompareOpen && (
              <div className="ml-10 mt-1 space-y-1 border-l-2 border-background-200 pl-4">
                <Link to="/spreadsheet-comparison" onClick={closeMobile} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-1 h-1 rounded-full bg-accent-400"></span>
                  Full Comparison Hub
                </Link>
                <Link to="/kakobuy-vs-cssbuy" onClick={closeMobile} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-1 h-1 rounded-full bg-accent-400"></span>
                  Kakobuy vs CSSBuy
                </Link>
                <Link to="/kakobuy-vs-superbuy" onClick={closeMobile} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-1 h-1 rounded-full bg-accent-400"></span>
                  Kakobuy vs Superbuy
                </Link>
                <Link to="/kakobuy-vs-oopbuy" onClick={closeMobile} className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                  <span className="w-1 h-1 rounded-full bg-accent-400"></span>
                  Kakobuy vs Oopbuy
                </Link>
              </div>
            )}
          </div>

          <div className="pt-3">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
                Shop by Category
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="ml-10 mt-1 space-y-0.5 border-l-2 border-background-200 pl-4">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={closeMobile}
                  >
                    <i className={`${categoryIcons[cat.name] || "ri-arrow-right-line"} text-sm text-foreground-400 w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                    <i className="ri-arrow-right-up-line text-[10px] text-foreground-300 ml-auto"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 space-y-3 px-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium border border-foreground-300 text-foreground-800 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-global-line"></i>
              Kakobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-bag-line"></i>
              Buy It Now
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}